import axios                 from 'axios';
import React                 from 'react';
import Image                 from '@widesk-ui/components/Image';
import { ImageProps }        from '@widesk-ui/components/Image';
import { blobToBase64Async } from '@widesk-core/tools/blobTools';
import { message }           from '@widesk-ui/hooks/useMessage';
import authStore             from '@widesk-core/stores/authStore';
import FileModel             from '@/models/services/file/StonecodeFileFileModel';

const cacheB64: Record<string, string | ArrayBuffer | null> = {};

export type FileImageProps = Omit<ImageProps, 'src'> & { file?: FileModel; };

const fetchBase64ForFile = async (file: FileModel) => {
	// `url` n'existe pas sur StonecodeFileFileModel : l'adresse du fichier est exposée
	// par le getter `downloadPath` (champ `@url` de l'API), comme plus bas dans FileImage.
	const src = file.downloadPath;

	if (cacheB64[src]) {
		return cacheB64[src];
	}

	const response = await axios.get(src, {
		headers: {
			'X-Platform-Authorization': `Bearer ${authStore.token}`,
		},
		responseType: 'blob',
	});

	const b64 = await blobToBase64Async(response.data);

	cacheB64[src] = b64;

	return b64;
};

const FileImage = (props: FileImageProps) => {
	const file = props.file || new FileModel();

	const [loading, setLoading] = React.useState(!!file.id && !file.isLoaded);

	const [base64, setBase64] = React.useState<unknown>();

	const src = file.downloadPath;
	const isPublic = file.public;

	React.useEffect(() => {
		(async () => {
			if (src && !isPublic) {
				try {
					setLoading(true);
					setBase64(await fetchBase64ForFile(file));
				} catch (error) {
					message.exception(error);
				} finally {
					setLoading(false);
				}
			} else {
				setBase64(undefined);
			}
		})();
	}, [src, isPublic]);

	const shownSrc = isPublic ? src : base64?.toString();

	// Adresse brute, sans CORS : voir `corsImageSrc`.
	return <Image loading={loading} src={shownSrc} {...props} />;
};

FileImage.fetchBase64ForFile = fetchBase64ForFile;

export default FileImage;
